// Layout: shared chrome (nav + footer + Ally). The route renders its content via <Outlet />.
// All content is injected via props — UI does not import from @content/.

import { useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Nav } from './Nav';
import { Footer } from './Footer';
import { Ally, type AllyCopy } from '@ui/ally/Ally';

export interface LayoutProps {
  documentTitle: string;
  pages: ReadonlyArray<readonly [string, string]>;
  links: {
    calendly: string;
    x: string;
    xhandle: string;
    linkedin: string;
    github: string;
    blog: string;
    resume: string;
  };
  ally: {
    context: string;
    greeting: string;
    suggestions: ReadonlyArray<string>;
    copy: AllyCopy;
  };
  showAlly?: boolean;
}

function useDocumentTitle(title: string) {
  useEffect(() => {
    const prev = document.title;
    document.title = title;
    return () => {
      document.title = prev;
    };
  }, [title]);
}

export function Layout({ documentTitle, pages, links, ally, showAlly = true }: LayoutProps) {
  const location = useLocation();
  useDocumentTitle(documentTitle);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [location.pathname]);

  return (
    <>
      <Nav pages={pages} calendlyHref={links.calendly} />
      <main>
        <Outlet />
      </main>
      <Footer
        xHref={links.x}
        xHandle={links.xhandle}
        linkedinHref={links.linkedin}
        githubHref={links.github}
        blogHref={links.blog}
        resumeHref={links.resume}
      />
      {showAlly && (
        <Ally
          context={ally.context}
          greeting={ally.greeting}
          suggestions={ally.suggestions}
          copy={ally.copy}
        />
      )}
    </>
  );
}
